import { type ApiErrorDetail, ApiErrorSchema } from '@/lib/schemas/common.schema';

// Clase de error unificada para fallos HTTP.
// Centraliza el mapeo del envelope NestJS al tipo que consume la UI/hooks.
export class ApiError extends Error {
  // Código HTTP devuelto por el backend (o 0 si no hubo respuesta parseable).
  readonly statusCode: number;
  // Identificador semántico devuelto por NestJS (`error` field). Útil para logging.
  readonly errorCode: string | undefined;
  // Lista de errores de validación por campo (cuando aplica).
  readonly details: ApiErrorDetail[] | undefined;
  // Payload estructurado del backend cuando `details` viene como object con
  // valores no-string (ej: 409 collision devuelve
  // `{ conflictingCategoryId: 518, conflictingCategoryName: 'Piñon' }`).
  // Los consumers de UI leen campos concretos para accionar sobre esa info.
  readonly structuredDetails: Record<string, unknown> | undefined;

  constructor(args: {
    statusCode: number;
    message: string;
    errorCode?: string;
    details?: ApiErrorDetail[];
    structuredDetails?: Record<string, unknown>;
  }) {
    super(args.message);
    this.name = 'ApiError';
    this.statusCode = args.statusCode;
    this.errorCode = args.errorCode;
    this.details = args.details;
    this.structuredDetails = args.structuredDetails;
    // Necesario para `instanceof` cuando se transpila a ES5/ES2015 con `extends Error`.
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  // Helpers de conveniencia para el branching en hooks/UI.
  get isUnauthorized(): boolean {
    return this.statusCode === 401;
  }

  get isForbidden(): boolean {
    return this.statusCode === 403;
  }

  get isNotFound(): boolean {
    return this.statusCode === 404;
  }

  get isRateLimited(): boolean {
    return this.statusCode === 429;
  }
}

// Extrae errores de validación bajo un `prefix` dado, devolviendo
// `Map<key, message>` donde key es el primer segmento después del prefix.
//
// Ej: prefix = "attributes.specifications.extendedProperties"
//   details = [{ field: "attributes.specifications.extendedProperties.gender", ... }]
//   → Map { "gender" => "..." }
//
//   details = [{ field: "attributes.specifications.extendedProperties.voltage.unit", ... }]
//   → Map { "voltage" => "..." } (colapsamos sub-paths al primer segmento)
//
// Usado por el editor de extendedProperties para mostrar hints inline en la
// fila correspondiente. Si el error no matchea con el prefix, se ignora.
export function extractFieldErrors(
  err: unknown,
  prefix: string,
): Map<string, string> {
  const out = new Map<string, string>();
  if (!(err instanceof ApiError) || !err.details) return out;
  for (const d of err.details) {
    if (!d.field) continue;
    if (!d.field.startsWith(prefix + '.')) continue;
    const rest = d.field.slice(prefix.length + 1);
    const key = rest.split('.')[0];
    if (key && !out.has(key)) out.set(key, d.message);
  }
  return out;
}

// Toma el body crudo de un fetch fallido y lo normaliza a `ApiError`.
// - Si el body matchea el envelope NestJS plano, usa esa info.
// - Si el body es envelope `{ data, error, meta }`, desempaca `error`.
// - Si no matchea, cae a un mensaje genérico construido desde el `Response`.
export function parseApiError(response: Response, body: unknown): ApiError {
  // Caso 1: envelope wrapper `{ data: null, error: {...}, meta: {...} }`.
  if (
    body !== null &&
    typeof body === 'object' &&
    'data' in body &&
    'error' in body &&
    'meta' in body
  ) {
    const inner = (body as { error: unknown }).error;
    const parsedInner = ApiErrorSchema.safeParse(inner);
    if (parsedInner.success) {
      const { statusCode, message, error, details, structuredDetails } =
        parsedInner.data;
      const flatMessage = Array.isArray(message) ? message.join(', ') : message;
      return new ApiError({
        statusCode: statusCode || response.status,
        message: flatMessage,
        errorCode: error,
        details,
        structuredDetails,
      });
    }
    // Envelope con error en formato no estándar (ej: string suelto).
    if (typeof inner === 'string' && inner.length > 0) {
      return new ApiError({ statusCode: response.status, message: inner });
    }
  }

  // Caso 2: envelope NestJS plano clásico.
  const parsed = ApiErrorSchema.safeParse(body);
  if (parsed.success) {
    const { statusCode, message, error, details, structuredDetails } =
      parsed.data;
    const flatMessage = Array.isArray(message) ? message.join(', ') : message;
    return new ApiError({
      statusCode,
      message: flatMessage,
      errorCode: error,
      details,
      structuredDetails,
    });
  }

  // Caso 3: fallback. 502 sin body JSON, 504, errores de gateway, etc.
  const fallbackMessage =
    response.statusText && response.statusText.length > 0
      ? response.statusText
      : `HTTP ${response.status}`;

  return new ApiError({
    statusCode: response.status,
    message: fallbackMessage,
  });
}
